import { format, isToday } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

export interface FormatDateOptions {
  /**
   * Which edge of the selected local day the UTC conversion should anchor to
   * when the date is today.
   *
   * - `'start'` (default) converts the start of the selected local day to UTC.
   *   Suitable for the start of a range: it yields the earliest UTC date any
   *   event on the user's local "today" can be stored under, so the lower bound
   *   never excludes early-in-the-day events.
   * - `'end'` converts the *current instant* ("now") to UTC. Suitable for the
   *   end of a range: it advances to the next UTC date only once the local
   *   clock actually crosses midnight UTC, so the upper bound tracks real time
   *   instead of jumping a day ahead.
   */
  boundary?: 'start' | 'end';
}

/**
 * Formats a Date as 'yyyy-MM-dd' for the backend.
 *
 * The backend stores each event as a UTC timestamp and filters by date range,
 * so a date that is today (local) is serialized in UTC to stay aligned with how
 * events are bucketed. The instant we convert depends on the boundary:
 *
 * - Start of range (`boundary: 'start'`, default): the start of the local day.
 *   In US Eastern, midnight EDT on Jul 24 is 04:00 UTC Jul 24 → "2026-07-24",
 *   so the lower bound includes every event from the user's local day.
 * - End of range (`boundary: 'end'`): the current moment. At 1pm EDT on Jul 24
 *   that is 17:00 UTC Jul 24 → "2026-07-24"; at 10pm EDT it is 02:00 UTC Jul 25
 *   → "2026-07-25", correctly extending the upper bound once late-evening
 *   events begin landing on the next UTC date. Converting *now* (rather than
 *   end-of-day) avoids sending tomorrow's UTC date earlier in the day.
 *
 * For historical dates (not today), we format in local time since the user
 * explicitly selected that calendar date and it represents a completed range.
 *
 * Note: the calendar date shown to the user (rendered from the raw Date in
 * local time) is unaffected — only the value sent to the backend applies the
 * UTC adjustment.
 */
const formatDate = (
  date?: Date,
  { boundary = 'start' }: FormatDateOptions = {}
) => {
  if (!date) return undefined;
  if (isToday(date)) {
    const instant = boundary === 'end' ? new Date() : date;
    return formatInTimeZone(instant, 'UTC', 'yyyy-MM-dd');
  }
  return format(date, 'yyyy-MM-dd');
};

export default formatDate;
